import React, { useState } from "react";
import { Stack, Button, List, ListItem, Typography, Divider, ListItemButton } from "@mui/material";
import { Search } from "@mui/icons-material";
import { Rank } from "@domain/insights/models/Rank";
import DialogHeader from "@shared/ui/dialog/DialogHeader";
import ForecastGrade from "../ForecastAnalytics/ForecastGrade";
import SearchTermSpotlight from "./SearchTermSpotlight";

interface RankSpotlightProps {
  ranks: Rank[];
  onSelect: (searchTerm: string) => void;
}

const RankSpotlight: React.FC<RankSpotlightProps> = ({
  ranks,
  onSelect,
}) => {
  const [showSearchTermSpotlight, setShowSearchTermSpotlight] = useState(false);

  const getAverageImpressions = (rank: Rank) => {
    const impressionsArr = rank.dailyProductRanks?.map(dailyProductRank => dailyProductRank.impressions ?? 0) ?? [];
    if (impressionsArr.length === 0) return 0;
    const sum = impressionsArr.reduce((acc, v) => acc + v, 0);
    return sum / impressionsArr.length;
  };

  const getAverageRank = (rank: Rank) => {
    const ranksArr = rank.dailyProductRanks?.map(dailyProductRank => dailyProductRank.rank ?? 0) ?? [];
    if (ranksArr.length === 0) return 0;
    const sum = ranksArr.reduce((acc, v) => acc + v, 0);
    return sum / ranksArr.length;
  };

  const handleSetRank = (searchTerm: string) => {
    onSelect(searchTerm);
    setShowSearchTermSpotlight(false);
  };

  return (
    <>
      <SearchTermSpotlight
        open={showSearchTermSpotlight}
        onClose={() => setShowSearchTermSpotlight(false)}
        onSelect={handleSetRank}
      />

      <DialogHeader>
        <Typography variant="subtitle2">Select Search Term</Typography>
      </DialogHeader>

      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        <Button
          startIcon={<Search />}
          onClick={() => setShowSearchTermSpotlight(true)}
          fullWidth
          variant="text"
          color="primary"
        >
          Search in all search terms
        </Button>
      </Stack>

      <Divider />

      <List disablePadding>
        {ranks
          ?.filter((rank, idx, arr) =>
            arr.findIndex(r => r.searchTerm === rank.searchTerm) === idx
          )
          ?.sort((a, b) => getAverageImpressions(b) - getAverageImpressions(a))
          .map((rank: Rank) => (
            <ListItemButton
              key={rank.id}
              onClick={() => onSelect(rank.searchTerm)}
              alignItems="flex-start"
              divider
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                <Stack direction="column" spacing={0.5} sx={{ minWidth: 160, flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {rank.searchTerm}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 88 }}
                    >
                      Impr: {getAverageImpressions(rank).toFixed(0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 70 }}
                    >
                      Rank: {getAverageRank(rank).toFixed(1)}
                    </Typography>
                  </Stack>
                </Stack>

                <ForecastGrade size="small" grade={rank.search.forecastGrade} />

              </Stack>
            </ListItemButton>
          ))}
      </List>
    </>
  );
};

export default RankSpotlight;
